'use client';

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { setRoleConfig } from '@/lib/roleConfig';
import {
  fetchRoleConfig,
  saveRoutePermission,
  deleteRoutePermission,
  deleteAppRole,
  saveAppRole,
} from '@/services/roleConfig/roleConfigService';

const ROLE_CONFIG_KEY = ['role-config'] as const;

/** Fetches the role config from ERP and keeps the module store in sync. */
export function useRoleConfig(enabled = true) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ROLE_CONFIG_KEY,
    queryFn: fetchRoleConfig,
    enabled,
    staleTime: 5 * 60 * 1000,   // 5 min — ERP caches for 60 s, so this is fine
    refetchOnWindowFocus: false,
  });

  // Push into the module store and signal all listeners to re-check permissions
  useEffect(() => {
    if (data) {
      setRoleConfig(data);
      window.dispatchEvent(new Event('routeConfigChanged'));
    }
  }, [data]);

  return { data, isLoading, isError, refetch };
}

export function useSaveRoutePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveRoutePermission,
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLE_CONFIG_KEY }),
  });
}

export function useDeleteRoutePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteRoutePermission,
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLE_CONFIG_KEY }),
  });
}

export function useDeleteAppRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteAppRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLE_CONFIG_KEY }),
  });
}

export function useSaveAppRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveAppRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLE_CONFIG_KEY }),
  });
}
